// export default CustomerManagement;
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import CustomerCard from "../components/CustomerCard";
import RedeemPointsModal from "../components/RedeemPointsModal";
import PurchaseCategorySelector from "../components/PurchaseCategorySelector";
import "../styles/CustomerManagement.css";
import {
  getAllCustomers,
  getEligibleCustomers,
  getTopCustomers,
  getRewardTiers,
  getTransactions,
  getPromotion,
  getAppSettings,
  deleteTransaction,
  updateCustomerName,
  updateCustomerInfo,
} from "../firebase";

const CustomerManagement = ({
  customers: initialCustomers,
  onSearchCustomer,
  onRegisterCustomer,
  onAddPurchase,
  selectedCustomer,
  transactions: initialTransactions,
  loading: parentLoading,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  // Search and new customer state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: "",
    lastName: "",
    city: "",
    amount: "",
    dateOfBirth: "",
    notes: "", // Added notes field for new customer
  });
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [purchaseNotes, setPurchaseNotes] = useState(""); // Added notes field for purchases
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);

  // Phone number suggestions
  const [suggestions, setSuggestions] = useState([]);
  const suggestionsRef = useRef(null);

  // Customer listing state
  const [selectedTab, setSelectedTab] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState(initialTransactions || []);

  // Promotions state
  const [promotions, setPromotions] = useState([]);
  const [activePromotion, setActivePromotion] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Redemption state
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [rewardTiers, setRewardTiers] = useState([]);

  // Purchase category state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCustomerCategory, setNewCustomerCategory] = useState(null);

  // Edit customer state
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({
    name: "",
    phone: "",
    dateOfBirth: "",
  });

  // Pagination state
  const [transactionPage, setTransactionPage] = useState(1);
  const [customerPage, setCustomerPage] = useState(1);
  const itemsPerPage = 10;

  // Delete transaction confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    transactionId: null,
    points: 0,
  });

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [newCustomerEmployee, setNewCustomerEmployee] = useState("");

  // Settings state
  const [settings, setSettings] = useState({});

  // Hardcoded tiered point system
  const pointTiers = [
    { min: 0, max: 99, rate: 0.02 }, // 0-99: 0.02
    { min: 100, max: 200, rate: 0.03 }, // 100-200: 0.03 points per dollar
    { min: 201, max: 600, rate: 0.03 }, // 201-600: 0.03 points per dollar
    { min: 601, max: 1500, rate: 0.02 }, // 601-1500: 0.02 points per dollar
    { min: 1501, max: Infinity, rate: 0.01 }, // 1501+: 0.01 points per dollar
  ];

  // Generate smart page numbers: 1 ... 4 5 [6] 7 8 ... 20
  const getPageNumbers = (currentPage, totalPages) => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, totalPages]);
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        result.push('...');
      }
      result.push(sorted[i]);
    }
    return result;
  };

  const renderPagination = (currentPage, setPage, totalItems) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;
    const pageNumbers = getPageNumbers(currentPage, totalPages);
    return (
      <div className="pagination">
        <button
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="page-button"
        >
          {isRTL ? '→' : '←'}
        </button>
        {pageNumbers.map((item, idx) =>
          item === '...' ? (
            <span key={`ellipsis-${idx}`} className="page-ellipsis">...</span>
          ) : (
            <button
              key={item}
              onClick={() => setPage(item)}
              className={`page-number ${currentPage === item ? 'active' : ''}`}
            >
              {item}
            </button>
          )
        )}
        <button
          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="page-button"
        >
          {isRTL ? '←' : '→'}
        </button>
        <span className="page-info">
          {currentPage} / {totalPages}
        </span>
      </div>
    );
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleNewCustomerCategorySelect = (category) => {
    setNewCustomerCategory(category);
  };

  // Start editing the customer's information
  const handleStartEditing = () => {
    if (selectedCustomer) {
      setEditedFields({
        name: selectedCustomer.name,
        phone: selectedCustomer.id, // Phone is stored as ID
        dateOfBirth: selectedCustomer.dateOfBirth
          ? new Date(selectedCustomer.dateOfBirth.toDate())
              .toISOString()
              .split("T")[0]
          : "",
      });
      setIsEditing(true);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditedFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedFields({
      name: "",
      phone: "",
      dateOfBirth: "",
    });
  };

  // Save the edited customer information
  const handleSaveEdit = async () => {
    if (!editedFields.name.trim()) {
      setError(t("notifications.validation.nameRequired") || "الاسم مطلوب");
      return;
    }

    if (!editedFields.phone.trim() || editedFields.phone.length < 10) {
      setError("رقم الهاتف يجب أن يكون على الأقل 10 أرقام");
      return;
    }

    try {
      setLoading(true);

      // Check if phone number changed
      const phoneChanged = editedFields.phone !== selectedCustomer.id;

      // If phone changed, need to check if the new number already exists
      if (phoneChanged) {
        const existingCustomer = await onSearchCustomer(editedFields.phone);
        if (existingCustomer && existingCustomer.id !== selectedCustomer.id) {
          setError("رقم الهاتف موجود بالفعل لعميل آخر");
          setLoading(false);
          return;
        }
      }

      // Update the customer data
      await updateCustomerInfo(
        selectedCustomer.id,
        editedFields.phone,
        editedFields.name,
        editedFields.dateOfBirth
      );

      // Refresh the customer data using the possibly new phone number
      const updatedCustomer = await onSearchCustomer(editedFields.phone);

      setSuccess("تم تحديث معلومات العميل بنجاح");
      setIsEditing(false);

      // Refresh customers list
      fetchCustomers();
    } catch (err) {
      setError(`فشل في تحديث معلومات العميل: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get points rate based on amount
  const getPointsRate = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 0;

    for (const tier of pointTiers) {
      if (numAmount >= tier.min && numAmount <= tier.max) {
        return tier.rate;
      }
    }
    return 0;
  };

  // Fetch app settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const appSettings = await getAppSettings();
        setSettings({
          // Keep other settings, but we'll ignore pointsPerDollar
          minRedemptionPoints: appSettings.minRedemptionPoints || 1000,
          expirationMonths: appSettings.expirationMonths || 12,
        });

        console.log("Loaded settings:", appSettings);
      } catch (err) {
        console.error("Error fetching app settings:", err);
        setError(
          t("notifications.error.loadSettings", { message: err.message })
        );
      }
    };

    fetchSettings();
  }, [t]);

  // Initialize with any customers passed in props
  useEffect(() => {
    if (initialCustomers && initialCustomers.length > 0) {
      setCustomers(initialCustomers);
    }
  }, [initialCustomers]);

  // Initialize with any transactions passed in props
  useEffect(() => {
    if (initialTransactions && initialTransactions.length > 0) {
      setTransactions(initialTransactions);
      setTransactionPage(1);
    }
  }, [initialTransactions]);

  // Fetch reward tiers for redemption functionality
  useEffect(() => {
    const fetchRewardTiers = async () => {
      try {
        const tiers = await getRewardTiers();
        setRewardTiers(tiers.filter((tier) => tier.active));
      } catch (err) {
        console.error("Error fetching reward tiers:", err);
        setError(
          t("notifications.error.loadRewards", { message: err.message })
        );
      }
    };

    fetchRewardTiers();
  }, [t]);

  // Fetch active promotions
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const allPromotions = await getPromotion();
        const now = new Date();

        // Filter for active promotions that are currently valid
        const validPromotions = allPromotions.filter((promo) => {
          if (!promo.active) return false;

          const startDate = new Date(promo.startDate);
          const endDate = new Date(promo.endDate);
          return now >= startDate && now <= endDate;
        });

        setPromotions(validPromotions);

        // Set the best promotion as active (highest multiplier)
        if (validPromotions.length > 0) {
          const bestPromo = validPromotions.reduce(
            (best, current) =>
              current.multiplier > best.multiplier ? current : best,
            validPromotions[0]
          );
          setActivePromotion(bestPromo);
        } else {
          setActivePromotion(null);
        }
      } catch (err) {
        console.error("Error fetching promotions:", err);
      }
    };

    fetchPromotions();
  }, []);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Wrap fetchCustomers in useCallback
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      let customerData;

      switch (selectedTab) {
        case "eligible":
          customerData = await getEligibleCustomers();
          break;
        case "top":
          customerData = await getTopCustomers(5);
          break;
        case "all":
        default:
          // Ensure we're always fetching fresh data
          customerData = await getAllCustomers();
          break;
      }

      // Update customers state with the fresh data
      setCustomers(customerData || []);
      setCustomerPage(1);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError(
        t("notifications.error.loadCustomers", { message: err.message })
      );
    } finally {
      setLoading(false);
    }
  }, [selectedTab, t]);

  // Fetch customers when tab changes or initialCustomers changes
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Handle phone number input change with suggestions
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);

    // Generate suggestions based on what the user types
    if (value.length > 0) {
      const matchingSuggestions = customers
        .filter(
          (customer) =>
            (customer.id && customer.id.includes(value)) ||
            (customer.name &&
              customer.name.toLowerCase().includes(value.toLowerCase()))
        )
        .slice(0, 5) // Limit to 5 suggestions
        .map((customer) => ({
          id: customer.id,
          name: customer.name,
        }));

      setSuggestions(matchingSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
      e.preventDefault();
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = async (customer) => {
    setPhoneNumber(customer.id);
    setSuggestions([]);
    setLoading(true);

    try {
      const result = await onSearchCustomer(customer.id);
      setSearchPerformed(true);

      if (result) {
        // Customer found
        setSuccess(
          t("notifications.success.customerFound", { name: result.name })
        );
        setShowNewCustomerForm(false);

        // Fetch latest transactions for this customer
        const freshTransactions = await getTransactions(customer.id);
        setTransactions(freshTransactions);
      } else {
        // Customer not found - this should not happen if we're selecting from suggestions
        setShowNewCustomerForm(true);
      }
    } catch (err) {
      setError(
        t("notifications.error.customerSearch", { message: err.message })
      );
    } finally {
      setLoading(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Clear search function
  const handleClearSearch = () => {
    setPhoneNumber("");
    setSearchPerformed(false);
    setShowNewCustomerForm(false);
    setSuggestions([]);
  };

  // Handle customer search
  const handleSearch = async () => {
    if (!phoneNumber.trim()) {
      setError(t("notifications.validation.phoneRequired"));
      return;
    }

    try {
      setSuggestions([]); // Clear suggestions when search is performed
      setLoading(true);
      const result = await onSearchCustomer(phoneNumber);
      setSearchPerformed(true);

      if (result) {
        // Customer found
        setSuccess(
          t("notifications.success.customerFound", { name: result.name })
        );
        setShowNewCustomerForm(false);

        // Fetch latest transactions for this customer
        const freshTransactions = await getTransactions(phoneNumber);
        setTransactions(freshTransactions);
      } else {
        // Customer not found
        setShowNewCustomerForm(true);
      }
    } catch (err) {
      setError(
        t("notifications.error.customerSearch", { message: err.message })
      );
    } finally {
      setLoading(false);
    }
  };

  const getPointRate = (amount) => {
    // Default rate if tiers aren't defined
    if (!pointTiers || !pointTiers.length) {
      return settings.pointsPerDollar || 10;
    }

    // Find the applicable tier for the amount
    const applicableTier = pointTiers.find((tier) => {
      return amount >= tier.min && (tier.max === null || amount <= tier.max);
    });

    // Return the tier rate if found, otherwise default rate
    return applicableTier
      ? applicableTier.rate
      : settings.pointsPerDollar || 10;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (
      !newCustomer.firstName ||
      !newCustomer.lastName ||
      !newCustomer.city ||
      !newCustomer.amount ||
      !newCustomer.dateOfBirth
    ) {
      setError("جميع الحقول مطلوبة");
      return;
    }

    if (!newCustomerEmployee) {
      setError("الرجاء اختيار الموظف");
      return;
    }

    if (!newCustomerCategory) {
      setError("الرجاء اختيار فئة المشتريات");
      return;
    }

    if (isNaN(newCustomer.amount) || parseFloat(newCustomer.amount) <= 0) {
      setError("المبلغ غير صالح");
      return;
    }

    try {
      // Combine first and last name
      const fullName = `${newCustomer.firstName} ${newCustomer.lastName}`;

      // Apply promotion multiplier if there's an active promotion
      const promotionInfo = activePromotion
        ? {
            promotionId: activePromotion.id,
            multiplier: activePromotion.multiplier,
          }
        : null;

      const customerData = {
        ...newCustomer,
        name: fullName, // Use the combined name field
        promotion: promotionInfo,
        pointsPerDollar: newCustomerCategory.pointsRate, // Use the selected category's rate
        employeeName: newCustomerEmployee,
        category: newCustomerCategory, // Pass the entire category object
        notes: newCustomer.notes, // Pass the notes
      };

      const result = await onRegisterCustomer(phoneNumber, customerData);
      if (result) {
        let successMessage = t("notifications.success.customerRegistered", {
          name: result.name,
          points: result.totalPoints,
        });

        // Add category information to success message
        successMessage += ` (${newCustomerCategory.name}: ${(
          newCustomerCategory.pointsRate * 100
        ).toFixed(1)}%)`;

        if (activePromotion) {
          successMessage += ` (${activePromotion.name}: ${activePromotion.multiplier}x)`;
        }

        setSuccess(successMessage);
        setNewCustomer({
          firstName: "",
          lastName: "",
          city: "",
          amount: "",
          dateOfBirth: "",
          notes: "",
        });
        setNewCustomerEmployee("");
        setNewCustomerCategory(null); // Reset category selection
        setShowNewCustomerForm(false);

        // Refresh the customer list after adding a new customer
        fetchCustomers();
      } else {
        setError(
          t("notifications.error.registration") || "فشل في تسجيل العميل"
        );
      }
    } catch (err) {
      setError(
        t("notifications.error.registration", { message: err.message }) ||
          `فشل في تسجيل العميل: ${err.message}`
      );
    }
  };

  const handleAddPurchase = async (e) => {
    e.preventDefault();

    if (!purchaseAmount) {
      setError(t("notifications.validation.amountRequired"));
      return;
    }

    if (!selectedEmployee) {
      setError(
        t("notifications.validation.employeeRequired") || "الرجاء اختيار الموظف"
      );
      return;
    }

    if (!selectedCategory) {
      setError("الرجاء اختيار فئة المشتريات");
      return;
    }

    if (isNaN(purchaseAmount) || parseFloat(purchaseAmount) <= 0) {
      setError(t("notifications.validation.invalidAmount"));
      return;
    }

    try {
      if (!selectedCustomer || !selectedCustomer.id) {
        setError(t("notifications.error.noCustomerSelected"));
        return;
      }

      // Convert to number explicitly
      const numericAmount = parseFloat(purchaseAmount);

      const pointsRate = selectedCategory.pointsRate;

      // Apply promotion if available
      let result;
      if (activePromotion) {
        result = await onAddPurchase(
          selectedCustomer.id,
          numericAmount,
          activePromotion.multiplier,
          selectedCategory.pointsRate,
          selectedEmployee,
          selectedCategory, // Pass the category object
          purchaseNotes // Pass the notes
        );
      } else {
        result = await onAddPurchase(
          selectedCustomer.id,
          numericAmount,
          1, // No promotion multiplier
          selectedCategory.pointsRate,
          selectedEmployee,
          selectedCategory, // Pass the category object
          purchaseNotes // Pass the notes
        );
      }

      if (result) {
        // Fetch the updated customer data
        const freshCustomerData = await onSearchCustomer(selectedCustomer.id);

        let successMessage = t("notifications.success.transactionRecorded", {
          points: result.pointsEarned,
        });

        // Add category information to success message
        successMessage += ` (${selectedCategory.name}: ${(
          selectedCategory.pointsRate * 100
        ).toFixed(1)}%)`;

        if (activePromotion) {
          const basePoints = Math.round(numericAmount * pointsRate);
          const bonusPoints = result.pointsEarned - basePoints;

          if (bonusPoints > 0) {
            successMessage += ` (${activePromotion.name}: +${bonusPoints})`;
          }
        }

        setSuccess(successMessage);
        setPurchaseAmount("");
        setPurchaseNotes(""); // Clear notes after successful purchase
        setSelectedEmployee(""); // Reset employee dropdown
        setSelectedCategory(null); // Reset category selection

        // Update transactions list
        const freshTransactions = await getTransactions(selectedCustomer.id);
        setTransactions(freshTransactions);

        // Refresh the customer list after adding a purchase
        fetchCustomers();
      } else {
        setError(t("notifications.error.addPurchase"));
      }
    } catch (err) {
      setError(t("notifications.error.addPurchase", { message: err.message }));
    }
  };

  // Select a customer by phone number
  const selectCustomer = async (phone) => {
    setPhoneNumber(phone);

    try {
      await onSearchCustomer(phone);

      // Fetch latest transactions for this customer
      const freshTransactions = await getTransactions(phone);
      setTransactions(freshTransactions);
    } catch (err) {
      setError(
        t("notifications.error.customerSearch", { message: err.message })
      );
    }
  };

  // Handle initiating transaction deletion with confirmation
  const handleDeleteTransactionInit = (transaction) => {
    // Show confirmation modal/dialog
    setDeleteConfirmation({
      show: true,
      transactionId: transaction.id,
      points: transaction.points,
      transactionData: transaction,
    });
  };

  // Handle confirming transaction deletion
  const handleConfirmDeleteTransaction = async () => {
    if (!selectedCustomer || !deleteConfirmation.transactionId) return;

    setLoading(true);
    try {
      // Call the deleteTransaction function from firebase.js
      await deleteTransaction(
        selectedCustomer.id,
        deleteConfirmation.transactionId,
        deleteConfirmation.points,
        deleteConfirmation.transactionData
      );

      // Refresh customer data
      const freshCustomerData = await onSearchCustomer(selectedCustomer.id);

      // Refresh transactions list
      const freshTransactions = await getTransactions(selectedCustomer.id);
      setTransactions(freshTransactions);

      // Updated success message to reflect marking as deleted rather than removal
      setSuccess("تم تحديد المعاملة كملغية وتحديث رصيد النقاط بنجاح");

      // Refresh customers list to update points
      fetchCustomers();
    } catch (err) {
      setError(`فشل في إلغاء المعاملة: ${err.message}`);
    } finally {
      setLoading(false);
      // Reset confirmation state
      setDeleteConfirmation({
        show: false,
        transactionId: null,
        points: 0,
        transactionData: null,
      });
    }
  };

  // Handle canceling transaction deletion
  const handleCancelDeleteTransaction = () => {
    setDeleteConfirmation({
      show: false,
      transactionId: null,
      points: 0,
      transactionData: null,
    });
  };

  const calculateEstimatedPointsForNewCustomer = (amount, rate) => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0 || !rate) return 0;

    const numericAmount = parseFloat(amount);

    // Calculate base points with the special rounding rule
    const rawPoints = numericAmount * rate;

    // Apply the special rounding: < 0.5 to 0, >= 0.5 to 1 for fractional part
    const basePoints = Math.floor(rawPoints) + (rawPoints % 1 >= 0.5 ? 1 : 0);

    // Apply promotion multiplier if active
    if (!activePromotion) return basePoints;

    // Calculate with multiplier and apply the same rounding rule
    const rawTotalPoints = basePoints * activePromotion.multiplier;
    return Math.floor(rawTotalPoints) + (rawTotalPoints % 1 >= 0.5 ? 1 : 0);
  };

  // Redemption functionality
  const handleOpenRedeemModal = () => {
    if (!selectedCustomer) {
      setError(t("notifications.error.noCustomerSelected"));
      return;
    }

    if (selectedCustomer.totalPoints < settings.minRedemptionPoints) {
      setError(
        t("notifications.error.notEnoughPoints", {
          minimum: settings.minRedemptionPoints,
        })
      );
      return;
    }

    setShowRedeemModal(true);
  };

  const handleCloseRedeemModal = () => {
    setShowRedeemModal(false);
  };

  const handleRedemptionSuccess = async (updatedCustomer) => {
    setShowRedeemModal(false);

    // Directly update the selected customer with the new data
    // This will trigger a re-render of the customer detail section
    const freshCustomerData = await onSearchCustomer(selectedCustomer.id);

    setSuccess(
      t("notifications.success.pointsRedeemed", {
        balance: updatedCustomer.totalPoints,
      })
    );

    // Refresh customer list to update points
    fetchCustomers();

    // If there's a selected customer, update their transactions
    if (freshCustomerData && freshCustomerData.id) {
      const freshTransactions = await getTransactions(freshCustomerData.id);
      setTransactions(freshTransactions);
    }
  };

  // Determine if we're loading
  const isLoading = loading || parentLoading;

  // Calculate estimated points for new purchase (including promotions)
  const calculateEstimatedPoints = (amount) => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) return 0;

    const numericAmount = parseFloat(amount);

    // Use the selected category's points rate if available, otherwise use tier system
    const pointsRate = selectedCategory
      ? selectedCategory.pointsRate
      : getPointsRate(numericAmount);

    // Calculate base points with the special rounding rule
    const rawPoints = numericAmount * pointsRate;

    // Apply the special rounding: <.5 to 0, >= .5 to 1 for fractional part
    const basePoints = Math.floor(rawPoints) + (rawPoints % 1 >= 0.5 ? 1 : 0);

    console.log(
      "Calculating points:",
      numericAmount,
      "×",
      pointsRate,
      "=",
      rawPoints,
      "→",
      basePoints
    );

    // Apply promotion multiplier if active
    if (!activePromotion) return basePoints;

    // Calculate with multiplier and apply the same rounding rule
    const rawTotalPoints = basePoints * activePromotion.multiplier;
    return Math.floor(rawTotalPoints) + (rawTotalPoints % 1 >= 0.5 ? 1 : 0);
  };

  const estimatedPoints = calculateEstimatedPoints(purchaseAmount);

  // Get current tier display for UI
  const getCurrentPointsRateTier = (amount) => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) return null;

    const numericAmount = parseFloat(amount);
    for (const tier of pointTiers) {
      if (numericAmount >= tier.min && numericAmount <= tier.max) {
        return {
          min: tier.min,
          max: tier.max === Infinity ? "∞" : tier.max,
          rate: tier.rate,
        };
      }
    }
    return null;
  };

  const currentTier = getCurrentPointsRateTier(purchaseAmount);
  const newCustomerTier = getCurrentPointsRateTier(newCustomer.amount);

  return (
    <div className={`customer-management-page ${isRTL ? "rtl" : ""}`}>
      <div className="page-title">
        <h2>{t("customers.title")}</h2>
        <p className="subtitle">{t("customers.subtitle")}</p>

        {/* Tiered Points System Info Display */}
        <div className="settings-info">
          <span className="settings-label">
            {t("customers.settingsInfo.minRedemption")}:
          </span>
          <span className="settings-value">
            {settings.minRedemptionPoints} {t("customers.points")}
          </span>
          {settings.expirationMonths > 0 && (
            <>
              <span className="settings-separator">•</span>
              <span className="settings-label">
                {t("customers.settingsInfo.expiration")}:
              </span>
              <span className="settings-value">
                {settings.expirationMonths} {t("customers.settingsInfo.months")}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Error and success messages */}
      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError("")} className="close-btn">
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <span>{success}</span>
          <button onClick={() => setSuccess("")} className="close-btn">
            ×
          </button>
        </div>
      )}

      {/* Delete Transaction Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="modal-backdrop">
          <div className="modal-content delete-confirmation-modal">
            <div className="modal-header">
              <h3 className="modal-title">تأكيد إلغاء المعاملة</h3>
              <button
                onClick={handleCancelDeleteTransaction}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>هل أنت متأكد من إلغاء هذه المعاملة؟</p>
              <p>
                سيتم إزالة{" "}
                <strong>{Math.abs(deleteConfirmation.points)}</strong> نقطة من
                رصيد العميل، وستظهر المعاملة كملغية في السجلات.
              </p>

              {deleteConfirmation.transactionData && (
                <div className="transaction-summary">
                  <div className="transaction-summary-item">
                    <span className="transaction-summary-label">التاريخ:</span>
                    <span className="transaction-summary-value">
                      {deleteConfirmation.transactionData.timestamp
                        ?.toDate()
                        .toLocaleDateString() || "-"}
                    </span>
                  </div>
                  <div className="transaction-summary-item">
                    <span className="transaction-summary-label">المبلغ:</span>
                    <span className="transaction-summary-value">
                      {deleteConfirmation.transactionData.amount
                        ? `₪${parseFloat(
                            deleteConfirmation.transactionData.amount
                          ).toFixed(0)}`
                        : "-"}
                    </span>
                  </div>
                  <div className="transaction-summary-item">
                    <span className="transaction-summary-label">النقاط:</span>
                    <span className="transaction-summary-value">
                      {deleteConfirmation.points}
                    </span>
                  </div>
                </div>
              )}

              <div className="warning-message">
                <p>تحذير: هذا الإجراء لا يمكن التراجع عنه!</p>
              </div>

              <div className="modal-actions">
                <button
                  onClick={handleCancelDeleteTransaction}
                  className="cancel-button"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleConfirmDeleteTransaction}
                  className="delete-button"
                >
                  {loading ? "جاري الإلغاء..." : "تأكيد الإلغاء"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active promotion banner */}
      {activePromotion && (
        <div className="promotion-banner">
          <div className="promotion-icon">🎉</div>
          <div className="promotion-details">
            <div className="promotion-title">{activePromotion.name}</div>
            <div className="promotion-info">
              {t("customers.promotion.multiplier", {
                multiplier: activePromotion.multiplier,
              })}
              <span className="promotion-period">
                ({t("customers.promotion.validUntil")}{" "}
                {new Date(activePromotion.endDate).toLocaleDateString(
                  i18n.language
                )}
                )
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Customer search section */}
      <div className="customer-search-section">
        <div className="search-container">
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="primary-button"
          >
            {isLoading
              ? t("customers.search.searching")
              : t("customers.search.button")}
          </button>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder={t("customers.search.placeholder")}
              className="input-field"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              onKeyDown={handleKeyDown}
            />
            {phoneNumber && (
              <button
                className="clear-button"
                onClick={handleClearSearch}
                title={t("customers.search.clear")}
              >
                ×
              </button>
            )}

            {/* Suggestions dropdown with customer name and ID */}
            {suggestions.length > 0 && (
              <div className="suggestions-dropdown" ref={suggestionsRef}>
                {suggestions.map((customer, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSelectSuggestion(customer)}
                  >
                    <span className="suggestion-name">{customer.name}</span>
                    <span className="suggestion-phone">{customer.id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customer Detail and Forms Section */}
      <div className="customer-forms-section">
        {/* New Customer Registration Form */}
        {showNewCustomerForm && searchPerformed && (
          <div className="form-card">
            <h3 className="form-title">{t("customers.newCustomer.title")}</h3>
            <form onSubmit={handleRegister}>
              <div className="form-group">
                <label className="form-label">
                  {"الاسم الأول"}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={newCustomer.firstName}
                  onChange={(e) =>
                    setNewCustomer({
                      ...newCustomer,
                      firstName: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {"اسم العائلة"}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={newCustomer.lastName}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, lastName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {t("customers.newCustomer.cityLabel")}
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={newCustomer.city}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, city: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {t("customers.newCustomer.dateOfBirthLabel")}
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={newCustomer.dateOfBirth}
                  onChange={(e) =>
                    setNewCustomer({
                      ...newCustomer,
                      dateOfBirth: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  فئة المشتريات
                  <span className="required-asterisk">*</span>
                </label>
                <PurchaseCategorySelector
                  selectedCategory={
                    newCustomerCategory ? newCustomerCategory.id : null
                  }
                  onSelectCategory={handleNewCustomerCategorySelect}
                  isRTL={isRTL}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {t("customers.newCustomer.amountLabel")}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field"
                  value={newCustomer.amount}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, amount: e.target.value })
                  }
                  required
                />
                {newCustomer.amount > 0 && newCustomerCategory && (
                  <div className="points-preview">
                    <span className="points-preview-label">
                      {t("customers.newCustomer.estimatedPoints")}:
                    </span>
                    <span className="points-preview-value">
                      {calculateEstimatedPointsForNewCustomer(
                        newCustomer.amount,
                        newCustomerCategory.pointsRate
                      )}
                    </span>
                    <span className="points-preview-tier">
                      ({newCustomerCategory.name}:{" "}
                      {(newCustomerCategory.pointsRate * 100).toFixed(1)}%)
                    </span>
                    {activePromotion && (
                      <span className="points-preview-bonus">
                        (
                        {t("customers.newCustomer.includesBonus", {
                          multiplier: activePromotion.multiplier,
                        })}
                        )
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Add notes field for new customer */}
              <div className="form-group">
                <label className="form-label">ملاحظات</label>
                <textarea
                  className="form-textarea"
                  rows="3"
                  value={newCustomer.notes}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, notes: e.target.value })
                  }
                  placeholder="أضف ملاحظات حول العميل أو المعاملة هنا"
                ></textarea>
              </div>

              {/* Employee dropdown for new customer */}
              <div className="form-group">
                <label className="form-label">
                  {t("customers.newCustomer.employeeLabel") || "الموظف"}
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--mid-gray)",
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: "white",
                    transition: "border-color 0.2s",
                  }}
                  value={newCustomerEmployee}
                  onChange={(e) => setNewCustomerEmployee(e.target.value)}
                  required
                >
                  <option value="">{"موظف المبيعات"}</option>
                  <option value="سامر">1 سامر</option>
                  <option value="بديع">2 بديع</option>
                  <option value="احمد منصور">7 احمد منصور</option>
                  <option value="حازم">3 حازم</option>
                  <option value="مجد">11 مجد</option>
                  <option value="أمجد غنام">9 أمجد غنام</option>
                  <option value="احمد السيد">5 احمد السيد</option>
                  <option value="هناء">4 هناء</option>
                  <option value="سحر">27 سحر</option>
                  <option value="نضال">28 نضال</option>
                  <option value="أبو طربوش">21 أبو طربوش</option>
                  <option value="طرود اونلاين">طرود اونلاين</option>
                  <option value="Gaming">Gaming</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isLoading || !newCustomerEmployee}
                className="accent-button full-width"
              >
                {isLoading
                  ? t("customers.newCustomer.processing")
                  : t("customers.newCustomer.button")}
              </button>
            </form>
          </div>
        )}

        {/* Selected Customer Detail Card */}
        {selectedCustomer && (
          <div className="customer-detail-section">
            <div className="customer-detail-card">
              <div className="customer-header styled-header">
                {isEditing ? (
                  <div className="edit-form">
                    <div className="form-group">
                      <label className="form-label">الاسم</label>
                      <input
                        type="text"
                        className="input-field"
                        value={editedFields.name}
                        onChange={(e) =>
                          handleEditFieldChange("name", e.target.value)
                        }
                        placeholder="أدخل الاسم"
                        autoFocus
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">رقم الهاتف</label>
                      <input
                        type="text"
                        className="input-field"
                        value={editedFields.phone}
                        onChange={(e) =>
                          handleEditFieldChange("phone", e.target.value)
                        }
                        placeholder="أدخل رقم الهاتف"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">تاريخ الميلاد</label>
                      <input
                        type="date"
                        className="input-field"
                        value={editedFields.dateOfBirth}
                        onChange={(e) =>
                          handleEditFieldChange("dateOfBirth", e.target.value)
                        }
                      />
                    </div>

                    <div className="edit-actions">
                      <button
                        onClick={handleSaveEdit}
                        className="accent-button"
                        disabled={isLoading}
                      >
                        {isLoading ? "جاري الحفظ..." : "حفظ المعلومات"}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="secondary-button"
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="customer-name">{selectedCustomer.name}</h3>
                    <button
                      onClick={handleStartEditing}
                      className="edit-button"
                      title="تعديل معلومات العميل"
                    >
                      ✎
                    </button>
                    <div className="customer-meta">
                      <span className="customer-phone">
                        {selectedCustomer.id}
                      </span>
                    </div>
                    <div className="customer-meta">
                      <span className="customer-city">
                        {selectedCustomer.city}
                      </span>
                      {selectedCustomer.dateOfBirth && (
                        <span className="customer-birthdate">
                          • تاريخ الميلاد:{" "}
                          {selectedCustomer.dateOfBirth
                            .toDate()
                            .toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="customer-points-display">
                <div>
                  <div className="points-value">
                    {selectedCustomer.totalPoints}
                  </div>
                  <div className="points-label">
                    {t("customers.customerDetails.points")}
                  </div>
                </div>

                {selectedCustomer.totalPoints >=
                settings.minRedemptionPoints ? (
                  <div className="points-actions">
                    <div className="redemption-badge large">
                      {t("customers.customerDetails.eligible")}
                    </div>
                    <button
                      onClick={handleOpenRedeemModal}
                      className="accent-button"
                    >
                      {t("customers.customerDetails.redeemButton")}
                    </button>
                  </div>
                ) : (
                  <div className="points-progress">
                    <div className="progress-text">
                      {t("customers.customerDetails.needsMore", {
                        points:
                          settings.minRedemptionPoints -
                          selectedCustomer.totalPoints,
                      })}
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.min(
                            100,
                            (selectedCustomer.totalPoints /
                              settings.minRedemptionPoints) *
                              100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* New Purchase Form */}
              <div className="purchase-form-section">
                <h4 className="form-subtitle">
                  {t("customers.customerDetails.newPurchase.title")}
                </h4>
                <form onSubmit={handleAddPurchase} className="purchase-form">
                  {/* Inside the purchase form, after the employee dropdown */}
                  <div className="form-group">
                    <PurchaseCategorySelector
                      selectedCategory={
                        selectedCategory ? selectedCategory.id : null
                      }
                      onSelectCategory={handleCategorySelect}
                      isRTL={isRTL}
                    />
                  </div>
                  <div className="form-row">
                    <button
                      type="submit"
                      disabled={isLoading || !selectedEmployee}
                      className="accent-button"
                    >
                      {isLoading
                        ? t("customers.customerDetails.newPurchase.processing")
                        : t("customers.customerDetails.newPurchase.button")}
                    </button>
                    <div className="input-with-points-preview">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={t(
                          "customers.customerDetails.newPurchase.placeholder"
                        )}
                        className="input-field"
                        value={purchaseAmount}
                        onChange={(e) => setPurchaseAmount(e.target.value)}
                      />
                      {purchaseAmount > 0 && (
                        <div className="points-estimate-bubble">
                          +{estimatedPoints} {t("customers.points")}
                          {activePromotion && (
                            <span className="points-multiplier">
                              ({activePromotion.multiplier}×)
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Employee dropdown */}
                    <div className="employee-select-container">
                      <select
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid var(--mid-gray)",
                          borderRadius: "8px",
                          fontSize: "14px",
                          backgroundColor: "white",
                          transition: "border-color 0.2s",
                          marginRight: "8px",
                        }}
                        value={selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                        required
                      >
                        <option value="">{"موظف المبيعات"}</option>
                        <option value="سامر">1 سامر</option>
                        <option value="بديع">2 بديع</option>
                        <option value="احمد منصور">7 احمد منصور</option>
                        <option value="حازم">3 حازم</option>
                        <option value="مجد">11 مجد</option>
                        <option value="أمجد غنام">9 أمجد غنام</option>
                        <option value="احمد السيد">5 احمد السيد</option>
                        <option value="هناء">4 هناء</option>
                        <option value="سحر">27 سحر</option>
                        <option value="نضال">28 نضال</option>
                        <option value="أبو طربوش">21 أبو طربوش</option>
                        <option value="طرود اونلاين">طرود اونلاين</option>
                        <option value="Gaming">Gaming</option>
                      </select>
                    </div>
                  </div>

                  {/* Add notes field for purchases */}
                  <div className="form-group">
                    <label className="form-label">ملاحظات</label>
                    <textarea
                      className="form-textarea"
                      rows="3"
                      value={purchaseNotes}
                      onChange={(e) => setPurchaseNotes(e.target.value)}
                      placeholder="أضف ملاحظات حول المعاملة هنا"
                    ></textarea>
                  </div>
                </form>
              </div>

              {/* Transaction History */}
              <div className="transactions-section">
                <h4 className="form-subtitle">
                  {t("customers.customerDetails.transactions.title")}
                </h4>
                {transactions && transactions.length > 0 ? (
                  <>
                  <div className="transactions-table">
                    <div className="table-header">
                      <div className="header-cell">
                        {t("customers.customerDetails.transactions.date")}
                      </div>
                      <div className="header-cell">
                        {t("customers.customerDetails.transactions.amount")}
                      </div>
                      <div className="header-cell">
                        {t("customers.customerDetails.transactions.points")}
                      </div>
                      <div className="header-cell">
                        {t("customers.customerDetails.transactions.notes")}
                      </div>
                      <div className="header-cell">الإجراءات</div>
                    </div>
                    {transactions
                      .slice((transactionPage - 1) * itemsPerPage, transactionPage * itemsPerPage)
                      .map((transaction) => (
                      <div
                        className={`table-row ${
                          transaction.isDeleted ? "deleted-transaction" : ""
                        }`}
                        key={transaction.id}
                      >
                        <div className="table-cell date-cell">
                          {transaction.timestamp
                            ?.toDate()
                            .toLocaleDateString(i18n.language) ||
                            t("common.recent")}
                        </div>
                        <div className="table-cell">
                          {transaction.isRedemption
                            ? t("customers.redemption")
                            : `${isRTL ? "" : "₪"}${parseFloat(
                                transaction.amount
                              ).toFixed(0)}${isRTL ? "₪" : ""}`}
                        </div>
                        <div
                          className={`table-cell ${
                            transaction.isRedemption ? "negative-points" : ""
                          }`}
                        >
                          {transaction.isDeleted ? (
                            <span className="deleted-points">
                              <s>
                                {transaction.isRedemption
                                  ? `-${Math.abs(transaction.points)}`
                                  : transaction.points}
                              </s>
                            </span>
                          ) : (
                            <>
                              {transaction.isRedemption
                                ? `-${Math.abs(transaction.points)}`
                                : transaction.points}
                              {transaction.promotionApplied && (
                                <span className="promotion-tag">
                                  {t("customers.promo")}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="table-cell">
                          {transaction.notificationSent &&
                            !transaction.isRedemption && (
                              <span className="reward-badge small">
                                {t("customers.eligible")}
                              </span>
                            )}
                          {transaction.isRedemption &&
                            transaction.rewardName && (
                              <span className="redemption-tag">
                                {transaction.rewardName}
                              </span>
                            )}
                          {transaction.employeeName && (
                            <span className="employee-tag">
                              {transaction.employeeName}
                            </span>
                          )}
                          {transaction.isDeleted && (
                            <span className="deleted-tag">ملغية</span>
                          )}
                          {/* Show notes indicator if transaction has notes */}
                          {transaction.notes && (
                            <span
                              className="notes-tag"
                              title={transaction.notes}
                            >
                              📝
                            </span>
                          )}
                        </div>
                        <div className="table-cell actions-cell">
                          {!transaction.isDeleted && (
                            <button
                              onClick={() =>
                                handleDeleteTransactionInit(transaction)
                              }
                              className="delete-transaction-button"
                              title="حذف المعاملة"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {renderPagination(transactionPage, setTransactionPage, transactions.length)}
                  </>
                ) : (
                  <p className="empty-state">
                    {t("customers.customerDetails.transactions.empty")}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Customer Directory */}
      <div className="customer-directory-section">
        <div className="directory-header">
          <h3 className="section-title">{t("customers.directory.title")}</h3>
          <div className="directory-tabs">
            <button
              className={`tab-button ${selectedTab === "all" ? "active" : ""}`}
              onClick={() => setSelectedTab("all")}
            >
              {t("customers.directory.tabs.all")}
            </button>
            <button
              className={`tab-button ${
                selectedTab === "eligible" ? "active" : ""
              }`}
              onClick={() => setSelectedTab("eligible")}
            >
              {t("customers.directory.tabs.eligible")}
            </button>
            <button
              className={`tab-button ${selectedTab === "top" ? "active" : ""}`}
              onClick={() => setSelectedTab("top")}
            >
              {t("customers.directory.tabs.top")}
            </button>
          </div>
        </div>
        <div className="customer-summary">
          <div className="summary-item">
            <span className="summary-value">{customers.length}</span>
            <span className="summary-label">
              {selectedTab === "all"
                ? t("customers.directory.summary.total")
                : selectedTab === "eligible"
                ? t("customers.directory.summary.eligible")
                : t("customers.directory.summary.top")}
            </span>
          </div>

          {selectedTab === "all" && customers.length > 0 && (
            <div className="summary-item">
              <span className="summary-value">
                {
                  customers.filter(
                    (c) => c.totalPoints >= settings.minRedemptionPoints
                  ).length
                }
              </span>
              <span className="summary-label">
                {t("customers.directory.summary.eligible")}
              </span>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="loading-container">{t("common.loading")}</div>
        ) : (
          <>
          <div className="enhanced-table customer-table">
            <div className="enhanced-table-header">
              <div className="header-cell">{"الاسم" || "الاسم"}</div>
              <div className="header-cell">{"رقم الهاتف" || "رقم الهاتف"}</div>
              <div className="header-cell">{"المدينة" || "المدينة"}</div>
              <div className="header-cell">{"النقاط" || "النقاط"}</div>
              <div className="header-cell">{"الحالة" || "الحالة"}</div>
              <div className="header-cell">{"التفاصيل" || "التفاصيل"}</div>
            </div>

            <div className="enhanced-table-body">
              {customers && customers.length > 0 ? (
                customers
                  .slice((customerPage - 1) * itemsPerPage, customerPage * itemsPerPage)
                  .map((customer) => (
                  <div
                    key={customer.id}
                    className="enhanced-table-row"
                    onClick={() => selectCustomer(customer.id)}
                  >
                    <div className="table-cell customer-name-cell">
                      {customer.name}
                    </div>
                    <div className="table-cell">{customer.id}</div>
                    <div className="table-cell">{customer.city}</div>
                    <div className="table-cell points-cell">
                      {customer.totalPoints}
                    </div>
                    <div className="table-cell">
                      {customer.totalPoints >= settings.minRedemptionPoints ? (
                        <span className="status-badge reward">
                          {t("customers.eligible")}
                        </span>
                      ) : null}
                    </div>
                    <div className="table-cell actions-cell">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          selectCustomer(customer.id);
                        }}
                        className="icon-button"
                        aria-label={t("customers.actions.select") || "Select"}
                      >
                        →
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  {t("customers.directory.empty")}
                  {selectedTab === "all" && (
                    <button
                      onClick={fetchCustomers}
                      className="primary-button"
                      style={{ marginLeft: "10px" }}
                    >
                      {t("customers.directory.refresh")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {renderPagination(customerPage, setCustomerPage, customers.length)}
          </>
        )}
      </div>

      {/* Redemption Modal */}
      {showRedeemModal && selectedCustomer && (
        <RedeemPointsModal
          customer={selectedCustomer}
          rewardTiers={rewardTiers}
          onClose={handleCloseRedeemModal}
          onSuccess={handleRedemptionSuccess}
          minRedemptionPoints={settings.minRedemptionPoints}
          isRTL={isRTL}
        />
      )}

      <style jsx>{`
        /* Style for the notes field */
        .form-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--mid-gray);
          border-radius: 8px;
          font-family: inherit;
          font-size: 14px;
          resize: vertical;
          transition: border-color 0.2s;
        }

        .form-textarea:focus {
          border-color: var(--primary-color);
          outline: none;
        }

        /* Style for the notes indicator in transaction list */
        .notes-tag {
          display: inline-block;
          margin-left: 4px;
          font-size: 14px;
          cursor: help;
        }

        /* Add responsive styles for smaller screens */
        @media (max-width: 768px) {
          .form-textarea {
            padding: 8px 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomerManagement;
